'use client';

import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import PageFooter from '../../components/shared/PageFooter';

/**
 * Documentation page for the Tabster MCP connector — linked from the Claude
 * and ChatGPT directory listings. Mirrors the privacy page's layout: sparkle
 * hero, sticky side index (desktop), same heading/body typography.
 */

const SECTIONS = [
  { id: 'overview', title: 'What the Connector Does' },
  { id: 'connect-claude', title: 'Connect in Claude' },
  { id: 'connect-chatgpt', title: 'Connect in ChatGPT' },
  { id: 'prompts', title: 'Things to Say' },
  { id: 'data', title: 'Your Data' },
  { id: 'support', title: 'Support' },
];

const ConnectContent = () => {
  const font = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const h2Style: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: '700',
    color: 'white',
    marginBottom: '16px',
    paddingTop: '8px',
  };

  const pStyle: React.CSSProperties = {
    fontSize: '15px',
    lineHeight: '1.85',
    color: 'rgba(255,255,255,0.65)',
    margin: '0 0 16px',
  };

  const ulStyle: React.CSSProperties = {
    paddingLeft: '20px',
    lineHeight: '1.85',
    fontSize: '15px',
    color: 'rgba(255,255,255,0.65)',
    margin: '0 0 16px',
  };

  const strongStyle: React.CSSProperties = {
    color: 'rgba(255,255,255,0.85)',
  };

  const codeStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 6,
    padding: '2px 7px',
    fontSize: '13.5px',
    color: '#8be9fd',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  };

  const linkStyle: React.CSSProperties = {
    color: '#06dffa',
    textDecoration: 'none',
  };

  return (
    <>
      <style>{`
        @keyframes connectSparkle {
          0%, 100% { background-position: 200% center; }
          50% { background-position: -200% center; }
        }

        .connect-layout {
          max-width: 680px;
          margin: 0 auto;
          padding: 0 24px;
        }
        .connect-sidebar {
          display: none;
        }
        .connect-content {
          width: 100%;
        }
        .connect-section {
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .connect-section:last-child {
          border-bottom: none;
        }

        .connect-sidebar-link {
          display: block;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 500;
          color: rgba(255,255,255,0.4);
          text-decoration: none;
          border-left: 2px solid transparent;
          transition: color 0.2s ease, border-color 0.2s ease;
          line-height: 1.4;
        }
        .connect-sidebar-link:hover {
          color: rgba(255,255,255,0.7);
        }
        .connect-sidebar-link.active {
          color: white;
          border-image: linear-gradient(180deg, #6ee7b7, #06dffa, #dcccfe) 1;
          font-weight: 600;
        }

        @media (min-width: 900px) {
          .connect-layout {
            max-width: 1060px !important;
            padding: 0 48px !important;
            display: flex !important;
            gap: 48px !important;
            align-items: flex-start !important;
          }
          .connect-sidebar {
            display: block !important;
            position: sticky !important;
            top: 32px !important;
            width: 240px !important;
            min-width: 240px !important;
            padding-top: 8px !important;
          }
          .connect-content {
            flex: 1 !important;
            min-width: 0 !important;
          }
          .connect-hero-title {
            font-size: 40px !important;
            text-align: left !important;
            margin-top: 72px !important;
          }
          .connect-hero-subtitle {
            text-align: left !important;
            font-size: 19px !important;
          }
          .connect-hero-intro {
            text-align: left !important;
          }
        }
      `}</style>
      <div
        style={{
          background:
            'linear-gradient(180deg, #0a0f32 0%, #0a1230 8%, #0c1a35 18%, #0e2240 30%, #102a48 45%, #0e2240 60%, #0c1a35 75%, #0a1230 88%, #0a0f32 100%)',
          minHeight: '100vh',
          color: '#fff',
          fontFamily: font,
        }}
      >
        <PageHeader />

        {/* Hero */}
        <div style={{ maxWidth: '1060px', margin: '0 auto', padding: '0 48px' }}>
          <h1
            className="connect-hero-title"
            style={{
              fontSize: '32px',
              // 600 to match the /text and /calculators heroes. At 800 this was
              // the only one of the three shouting.
              fontWeight: '600',
              marginTop: '36px',
              marginBottom: '8px',
              textAlign: 'center',
              fontFamily:
                'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
              background:
                'linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              animation: 'connectSparkle 3s ease-in-out infinite',
            }}
          >
            Tabster for Claude &amp; ChatGPT
          </h1>
          <p
            className="connect-hero-subtitle"
            style={{
              textAlign: 'center',
              fontSize: '17px',
              lineHeight: '1.7',
              fontWeight: '500',
              marginBottom: '12px',
              color: 'white',
            }}
          >
            Split the check without leaving your chat
          </p>
          <p
            className="connect-hero-intro"
            style={{
              textAlign: 'center',
              fontSize: '15px',
              color: 'rgba(255,255,255,0.55)',
              marginBottom: '24px',
              lineHeight: '1.8',
              maxWidth: '680px',
            }}
          >
            Send your assistant a receipt photo to receive a Tabster link. Friends tap what they
            ordered, tax and tip split automatically, and everyone pays back what they owe in
            seconds.
          </p>
        </div>

        {/* Main layout: sidebar + content */}
        <div className="connect-layout">
          <nav className="connect-sidebar">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`connect-sidebar-link ${activeSection === s.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {s.title}
              </a>
            ))}
          </nav>

          <div className="connect-content">
            <div id="overview" className="connect-section">
              <h2 style={h2Style}>What the Connector Does</h2>
              <ol style={ulStyle}>
                <li>
                  <strong style={strongStyle}>Snap:</strong> Send a receipt photo and say
                  &ldquo;split this.&rdquo;
                </li>
                <li>
                  <strong style={strongStyle}>Share:</strong> Get a link — friends open it, no
                  account needed.
                </li>
                <li>
                  <strong style={strongStyle}>Split:</strong> Everyone taps their items; tax and
                  tip divide automatically.
                </li>
              </ol>
            </div>

            <div id="connect-claude" className="connect-section">
              <h2 style={h2Style}>Connect in Claude</h2>
              <p style={pStyle}>
                <strong style={strongStyle}>Settings → Connectors</strong>, find{' '}
                <strong style={strongStyle}>Tabster</strong>, click Connect, and sign in with your
                phone number.
              </p>
              <p style={pStyle}>
                Not listed yet? Add it manually as a custom connector:{' '}
                <span style={codeStyle}>https://mcp.tabster.ai/mcp</span>
              </p>
            </div>

            <div id="connect-chatgpt" className="connect-section">
              <h2 style={h2Style}>Connect in ChatGPT</h2>
              <p style={pStyle}>
                <strong style={strongStyle}>Settings → Apps &amp; Connectors</strong>, find{' '}
                <strong style={strongStyle}>Tabster</strong>, and connect your account. In a chat,
                add Tabster from the + menu and send a receipt photo.
              </p>
              <p style={pStyle}>
                Not listed yet? With developer mode on, add a custom connector:{' '}
                <span style={codeStyle}>https://mcp.tabster.ai/mcp</span> (auth: OAuth).
              </p>
            </div>

            <div id="prompts" className="connect-section">
              <h2 style={h2Style}>Things to Say</h2>
              <ul style={ulStyle}>
                <li>&ldquo;Split this receipt between me, Alice, and Bob&rdquo; + a photo</li>
                <li>&ldquo;Who owes what for this?&rdquo; + a photo</li>
                <li>&ldquo;Divvy up dinner between the four of us, 20% tip&rdquo;</li>
                <li>&ldquo;Help me split $60 of pizza three ways&rdquo;</li>
              </ul>
            </div>

            <div id="data" className="connect-section">
              <h2 style={h2Style}>Your Data</h2>
              <ul style={ulStyle}>
                <li>Splits save to your Tabster account.</li>
                <li>Links are visible only to people you share them with.</li>
                <li>Tabster never moves money.</li>
              </ul>
              <p style={pStyle}>
                Full details in our{' '}
                <a href="/privacy" style={linkStyle}>
                  Privacy Policy
                </a>
                .
              </p>
            </div>

            <div id="support" className="connect-section">
              <h2 style={h2Style}>Support</h2>
              <p style={pStyle}>
                <a href="mailto:support@tabster.ai" style={linkStyle}>
                  support@tabster.ai
                </a>{' '}
                or{' '}
                <a href="/support" style={linkStyle}>
                  tabster.ai/support
                </a>
              </p>
            </div>
          </div>
        </div>

        <PageFooter />
      </div>
    </>
  );
};

export default ConnectContent;
